import Logo from "../Logo";
import {
  AuthSidebarVariant,
  getAuthFooterLinks,
  getAuthSidebarContent,
} from "../../branding/authSidebar";
import { themeClassName } from "../../utils/theme";

interface AuthSidebarProps {
  variant?: AuthSidebarVariant;
  /**
   * `panel` — full-height left column for login/signup split layouts.
   * `inline` — content-only block for invite-style half-page layouts.
   */
  layout?: "panel" | "inline";
}

export function AuthSidebar({
  variant = "default",
  layout = "panel",
}: AuthSidebarProps) {
  const theme = themeClassName();
  const content = getAuthSidebarContent(theme, variant);
  const logoWidth = content.logoWidth ?? 105;
  const logoHeight = content.logoHeight ?? 35;

  const logo = <Logo width={logoWidth} height={logoHeight} />;
  const websiteUrl = content.websiteUrl?.trim();

  const body = (
    <>
      <div>
        {websiteUrl ? (
          <a
            href={websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block w-fit transition-opacity hover:opacity-90"
            aria-label="Visit website"
          >
            {logo}
          </a>
        ) : (
          logo
        )}

        <h1 className="mt-8 text-3xl font-semibold tracking-tight text-white text-balance">
          {content.headline}
        </h1>

        {content.tagline ? (
          <p className="mt-2 text-base font-medium text-persian-200/80 text-pretty">
            {content.tagline}
          </p>
        ) : null}

        <p className="mt-5 text-[15px] leading-relaxed text-white/80 text-pretty">
          {content.body}
        </p>

        {content.quote ? (
          <figure className="mt-8 border-l border-persian-400/40 pl-4">
            <blockquote className="m-0 border-0 bg-transparent p-0 shadow-none text-[15px] leading-relaxed text-white/75 italic text-pretty">
              “{content.quote.text}”
            </blockquote>
            <figcaption className="mt-3 text-xs text-persian-200/80">
              <span className="font-semibold text-persian-100">
                {content.quote.attribution}
              </span>
              {content.quote.role ? (
                <span className="text-persian-300/70">
                  {" "}
                  · {content.quote.role}
                </span>
              ) : null}
            </figcaption>
          </figure>
        ) : null}

        {content.showcaseImages && content.showcaseImages.length > 0 ? (
          <div className="mt-8 grid grid-cols-3 gap-3">
            {content.showcaseImages.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                className="w-full aspect-[2/3] object-cover object-top rounded-lg ring-1 ring-white/10 shadow-lg shadow-black/30"
              />
            ))}
          </div>
        ) : null}
      </div>

      <AuthSidebarFooter />
    </>
  );

  if (layout === "inline") {
    return <div className="flex h-full flex-col justify-between">{body}</div>;
  }

  return (
    <div className="hidden h-full flex-1 flex-col justify-center bg-persian-950 xl:flex">
      <div className="mx-auto flex h-full w-full max-w-xl flex-col justify-between px-12 py-12 box-border">
        {body}
      </div>
    </div>
  );
}

function AuthSidebarFooter() {
  const footer = getAuthFooterLinks();

  return (
    <div className="mt-12 flex items-center justify-between gap-x-4">
      <p className="text-sm text-persian-300/80 shrink-0">
        Powered by{" "}
        <a
          href={footer.poweredByUrl}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-persian-100 hover:text-white transition-colors"
        >
          {footer.poweredByName}
        </a>
      </p>
      <div className="flex items-center gap-x-3">
        <a
          target="_blank"
          rel="noreferrer"
          href={footer.privacyUrl}
          className="text-sm font-semibold text-persian-200 hover:text-white transition-colors"
        >
          Privacy Policy
        </a>
        <span className="text-persian-500" aria-hidden>
          ·
        </span>
        <a
          target="_blank"
          rel="noreferrer"
          href={footer.termsUrl}
          className="text-sm font-semibold text-persian-200 hover:text-white transition-colors"
        >
          Terms & Conditions
        </a>
      </div>
    </div>
  );
}
